import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { SettingsService } from "../settings/service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import { Editor } from "@tinymce/tinymce-react";

interface PartnerPageSettingsState {
  partnerIntroText: string;
}

const PartnerPageSettingsForm: React.FC = () => {
  const { t } = useTranslation();
  const [settingsData, setSettingsData] =
    useState<PartnerPageSettingsState | null>(null);

  const { control, handleSubmit, reset } = useForm<PartnerPageSettingsState>({
    defaultValues: {
      partnerIntroText: "",
    },
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await SettingsService.getSingle();
        setSettingsData({ partnerIntroText: response.partnerIntroText ?? "" });
      } catch (error) {
        ErrorToast(error);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (settingsData) {
      reset(settingsData);
    }
  }, [settingsData, reset]);

  const onSubmit = async (data: PartnerPageSettingsState) => {
    try {
      await SettingsService.update(data);
      SuccessToast(t("partnerPageSettings.successUpdate"));
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col sm={12}>
                    <div className="mb-0">
                      <label className="form-label">
                        {t("partnerPageSettings.introText")}
                      </label>
                      <Controller
                        name="partnerIntroText"
                        control={control}
                        render={({ field }) => (
                          <Editor
                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                            value={field.value}
                            onEditorChange={(content: string) =>
                              field.onChange(content)
                            }
                            init={{
                              height: 360,
                              menubar: false,
                              branding: false,
                              plugins: [
                                "advlist",
                                "autolink",
                                "lists",
                                "link",
                                "image",
                                "charmap",
                                "preview",
                                "anchor",
                                "searchreplace",
                                "visualblocks",
                                "code",
                                "fullscreen",
                                "insertdatetime",
                                "media",
                                "table",
                                "help",
                                "wordcount",
                              ],
                              toolbar:
                                "undo redo | blocks | bold italic underline strikethrough | " +
                                "alignleft aligncenter alignright alignjustify | " +
                                "bullist numlist outdent indent | table | link image | removeformat | code",
                              content_style:
                                "body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }",
                            }}
                          />
                        )}
                      />
                    </div>
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t("partnerPageSettings.save")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default PartnerPageSettingsForm;
